/* eslint-disable no-redeclare */
/* eslint-disable no-undef */
/* eslint-disable default-case */
/* eslint-disable @typescript-eslint/no-shadow */
/*

This file is capital.
This context feeds data to the whole application. Take this away and nothing works anymore.

*/

// Imports. nothing to worry about here.
import {
  Building,
  Unit,
  Contact,
  Lease,
  BuildingOwner,
  UnitLease,
  UnitOwner,
  LeaseContact,
  Technic,
  TechnicLibrary,
  Ticket,
  Team,
  UserTeam,
  Address,
  BankAccount,
  UserRole,
  Notification,
  DateTime,
} from '@rentguru/commons-utils';
import { LoadingScreen } from '@up2rent/ui';
import isNil from 'lodash/isNil';
import React, { useEffect, useReducer, Reducer, useMemo, useState } from 'react';

import ReadAccessSyncError from 'src/components/Routes/ReadAccessSyncError';
import { waitForContactReadAccess } from 'src/utils/accessSyncUtils';

import { ActionAddress, addressReducerDelegation } from './AddressContext';
import { ActionBankAccount, bankAccountReducerDelegation } from './BankAccountsContext';
import { ActionBuilding, buildingReducerDelegation } from './BuildingsContext';
import {
  ActionBuildingOwner,
  ActionContact,
  ActionUnitOwner,
  buildingOwnerReducerDelegation,
  contactReducerDelegation,
  unitOwnerReducerDelegation,
} from './ContactsContext';
import { ActionLeaseContact, leaseContactReducerDelegation } from './LeaseContactContext';
import { ActionLease, leaseReducerDelegation } from './LeasesContext';
import { ActionNotification, notificationReducerDelegation } from './NotificationsContext';
import { ActionTeam, teamReducerDelegation } from './TeamsContext';
import { ActionTechnic, technicReducerDelegation } from './TechnicsContext';
import { ActionTechnicLibrary, technicLibraryReducerDelegation } from './TechnicsLibraryContext';
import { ActionTicket, ticketReducerDelegation } from './TicketsContext';
import { ActionUnitLease, unitLeaseReducerDelegation } from './UnitLeaseContext';
import { ActionUnit, unitReducerDelegation } from './UnitsContext';
import { useUser } from './UserContext';
import { ActionUserRole, userRoleReducerDelegation } from './UserRolesContext';
import {
  ActionUser,
  ActionUserTeam,
  userReducerDelegation,
  userTeamReducerDelegation,
  type WorkspaceUser,
} from './UsersContext';
import { filterContextLoaderBundleByTeam } from './utils';
import { emptyContextLoader, isContextLoaderLoading } from './utils/contextLoaderUtils';

/*
  This interface is essential to te well being of this context.
  the idea is to hold one and only state that will keep all the subscribed tables.
  This is going to get updated and rerender the whole application when one of those tables gets 
  an update (insert update or delete of a record)

  Address
  BankAccount
  Building
  ...
  ...
  UserTeam
  CustomSubscription

  are going to belocally stored (in localstorage). This makes it way faster to use and handle data
*/
export interface ContextLoaderStore {
  Address: { data: Address[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  BankAccount: { data: BankAccount[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  Building: { data: Building[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  BuildingOwner: { data: BuildingOwner[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  Contact: { data: Contact[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  Lease: { data: Lease[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  LeaseContact: { data: LeaseContact[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  Notification: { data: Notification[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  Team: { data: Team[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  Technic: { data: Technic[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  TechnicLibrary: { data: TechnicLibrary[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  Ticket: { data: Ticket[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  Unit: { data: Unit[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  UnitLease: { data: UnitLease[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  UnitOwner: { data: UnitOwner[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  User: { data: WorkspaceUser[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  UserRole: { data: UserRole[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
  UserTeam: { data: UserTeam[]; loading: boolean; shouldFetch: boolean; lastFetch: DateTime | null };
}

export type ContextLoaderModel = keyof ContextLoaderStore;

export type ContextLoaderAction =
  | { type: 'SHOULD_FETCH_ALL' }
  | { type: 'FETCH_FAILED'; payload: { model: ContextLoaderModel } }
  | ActionAddress
  | ActionBankAccount
  | ActionBuilding
  | ActionContact
  | ActionBuildingOwner
  | ActionLease
  | ActionLeaseContact
  | ActionNotification
  | ActionUnitOwner
  | ActionTeam
  | ActionTechnic
  | ActionTechnicLibrary
  | ActionTicket
  | ActionUnit
  | ActionUnitLease
  | ActionUser
  | ActionUserRole
  | ActionUserTeam;

const contextLoaderReducer = (state: ContextLoaderStore, action: ContextLoaderAction): ContextLoaderStore => {
  if (action.type === 'SHOULD_FETCH_ALL') {
    return Object.fromEntries(
      Object.entries(state).map(([key, value]) => [key, { ...value, shouldFetch: true }])
    ) as ContextLoaderStore;
  }

  if (action.type === 'FETCH_FAILED') {
    const failedModel = action.payload.model;
    return {
      ...state,
      [failedModel]: { ...state[failedModel], loading: false, shouldFetch: false },
    };
  }

  const addressState = addressReducerDelegation(state, action);
  const bankAccountState = bankAccountReducerDelegation(addressState, action);
  const buildingState = buildingReducerDelegation(bankAccountState, action);
  const contactState = contactReducerDelegation(buildingState, action);
  const leaseContactState = leaseContactReducerDelegation(contactState, action);
  const buildingOwnerState = buildingOwnerReducerDelegation(leaseContactState, action);
  const notificationState = notificationReducerDelegation(buildingOwnerState, action);
  const unitOwnerState = unitOwnerReducerDelegation(notificationState, action);
  const teamState = teamReducerDelegation(unitOwnerState, action);
  const technicState = technicReducerDelegation(teamState, action);
  const technicLibraryState = technicLibraryReducerDelegation(technicState, action);
  const ticketState = ticketReducerDelegation(technicLibraryState, action);
  const unitState = unitReducerDelegation(ticketState, action);
  const unitLeaseState = unitLeaseReducerDelegation(unitState, action);
  const userState = userReducerDelegation(unitLeaseState, action);
  const userRoleState = userRoleReducerDelegation(userState, action);
  const userTeamState = userTeamReducerDelegation(userRoleState, action);
  const leaseState = leaseReducerDelegation(userTeamState, action);

  const finalState = leaseState;
  return finalState;
};

interface ContextBundle extends ContextLoaderStore {
  dispatch: React.Dispatch<ContextLoaderAction>;
  loading: boolean;
}

// We create the actual context here. All the children of this component
// will be able to access the values of this context.
export const ContextLoader = React.createContext<ContextBundle | null>(null);

// We create the actual component here.
export const ContextLoaderProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const { clientId, isFetchingUser, rootUser, memberId, userAttributes, isOwner, isTenant, ownerId } = useUser();
  const teamMembershipUserId = memberId ?? userAttributes?.sub;
  const [ownerReadAccessGranted, setOwnerReadAccessGranted] = useState<boolean>(false);
  const [ownerReadAccessTimedOut, setOwnerReadAccessTimedOut] = useState<boolean>(false);
  const [ownerReadAccessAttempt, setOwnerReadAccessAttempt] = useState<number>(0);
  const waitingForOwnerReadAccess = isOwner && !isNil(ownerId) && !ownerReadAccessGranted;

  const [state, dispatch] = useReducer<Reducer<ContextLoaderStore, ContextLoaderAction>>(
    contextLoaderReducer,
    emptyContextLoader
  );

  const [fetchRequested, setFetchRequested] = useState<boolean>(false);
  const loading = isFetchingUser || isContextLoaderLoading(state, fetchRequested);
  const shouldFilterByTeam = !rootUser && !isOwner && !isTenant;

  useEffect(() => {
    if (isNil(clientId)) {
      return;
    }
    const requestFetchAll = () => {
      setFetchRequested(true);
      dispatch({ type: 'SHOULD_FETCH_ALL' });
    };
    if (!isOwner || isNil(ownerId)) {
      requestFetchAll();
      return;
    }
    let cancelled = false;
    const waitForOwnerAccessSync = async () => {
      const granted = await waitForContactReadAccess(ownerId);
      if (cancelled) {
        return;
      }
      if (!granted) {
        setOwnerReadAccessTimedOut(true);
        return;
      }
      setOwnerReadAccessGranted(true);
      requestFetchAll();
    };
    waitForOwnerAccessSync();
    return () => {
      cancelled = true;
    };
  }, [clientId, isOwner, ownerId, ownerReadAccessAttempt]);

  const values = useMemo<ContextBundle>(() => {
    const contextLoaderStore =
      shouldFilterByTeam && !loading && !isNil(teamMembershipUserId)
        ? filterContextLoaderBundleByTeam(state, teamMembershipUserId)
        : state;
    return { ...contextLoaderStore, loading, dispatch };
  }, [state, loading, shouldFilterByTeam, teamMembershipUserId]);

  if (ownerReadAccessTimedOut) {
    const retryOwnerReadAccess = () => {
      setOwnerReadAccessTimedOut(false);
      setOwnerReadAccessAttempt((attempt) => attempt + 1);
    };
    return <ReadAccessSyncError onRetry={retryOwnerReadAccess} />;
  }

  if (waitingForOwnerReadAccess) {
    return <LoadingScreen />;
  }

  return <ContextLoader.Provider value={values}>{children}</ContextLoader.Provider>;
};

interface ContextLoaderGateProps {
  children?: React.ReactNode;
}

export const ContextLoaderGate = ({ children }: ContextLoaderGateProps) => {
  const { rootUser } = useUser();
  const { loading } = useContextLoader();

  if (!rootUser && loading) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
};

export const useContextLoader = (): ContextBundle => {
  const context = React.useContext<ContextBundle | null>(ContextLoader);

  if (context === undefined) {
    throw new Error('`useContextLoader` hook must be used within a `ContextLoaderProvider` component');
  }
  return context as ContextBundle;
};
